// src/pages/Checkout.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  CreditCard, 
  Truck, 
  Shield, 
  Clock,
  MapPin,
  AlertCircle,
  Minus,
  Plus,
  Trash2,
  ExternalLink
} from 'lucide-react';

// Header is now in PublicLayout
import { useCart } from '../features/october/hooks/useCart';
import { usePayment } from '../features/october/hooks/usePayment';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, cartSummary, updateQuantity, removeFromCart } = useCart();
  const { 
    initiatePayment, 
    isProcessing, 
    error: paymentError, 
    isPayFastAvailable,
    clearError 
  } = usePayment();
  
  // Form state
  const [formData, setFormData] = useState({
    // Contact Information
    email: '',
    phone: '',
    
    // Shipping Address
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    
    // Options
    saveInfo: false,
    newsletter: false,
    notes: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/kganya-scents');
    }
  }, [cartItems.length, navigate]);


  // Clear payment errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Pre-fill form from a retry (PaymentCancelled sends prefillData via location.state)
  useEffect(() => {
    const prefill = location.state?.prefillData;
    if (prefill) {
      setFormData(prev => ({
        ...prev,
        email: prefill.email || prev.email,
        firstName: prefill.firstName || prev.firstName,
        lastName: prefill.lastName || prev.lastName,
        phone: prefill.phone || prev.phone,
        address: prefill.address || prev.address,
        city: prefill.city || prev.city,
        province: prefill.province || prev.province,
        postalCode: prefill.postalCode || prev.postalCode,
      }));
    }
  }, [location.state]);


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.address) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.province) newErrors.province = 'Province is required';
      if (!formData.postalCode) newErrors.postalCode = 'Postal code is required';
      
      // Email validation
      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStepNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 2));
    }
  };

  const handleStepBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(1)) return;
    
    try {
      // Check if PayFast is available
      if (!isPayFastAvailable()) {
        throw new Error('Payment service is not available. Please try again later.');
      }

      // Prepare customer data
      const customerData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        notes: formData.notes
      };

      // Prepare shipping data
      const shippingData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        postalCode: formData.postalCode
      };

      // Initiate payment (this will redirect to PayFast)
      await initiatePayment(customerData, shippingData);
      
    } catch (error) {
      console.error('Checkout error:', error);
      // Error will be displayed via paymentError state
    }
  };

  const steps = [
    { id: 1, name: 'Information', icon: MapPin },
    { id: 2, name: 'Payment', icon: CreditCard }
  ];

  if (cartItems.length === 0) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-brand-black">
      
      <div className="pt-20 pb-16 bg-brand-black backdrop-blur-xl min-h-screen">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/kganya-scents')}
                className="p-2 bg-white/10 backdrop-blur-md text-gray-400 hover:text-white hover:bg-white/20 rounded-xl transition-all border border-white/20"
              >
                <ArrowLeft size={20} strokeWidth={1.5} />
              </button>
              <h1 className="text-2xl lg:text-3xl font-light text-white tracking-wide">Checkout</h1>
            </div>
            
            <Link 
              to="/kganya-scents" 
              className="text-gray-400 hover:text-white text-sm font-light transition-colors"
            >
              Continue Shopping
            </Link>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex items-center gap-4">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                
                return (
                  <React.Fragment key={step.id}>
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-brand-red text-brand-light' 
                        : isCompleted 
                        ? 'bg-brand-red/40 text-brand-light' 
                        : 'bg-brand-black border border-brand-red/30 text-brand-light/60'
                    }`}>
                      <Icon size={20} strokeWidth={1.5} />
                      <span className="font-light tracking-wide">{step.name}</span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="w-8 h-px bg-brand-gold/25" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Error Display */}
          {paymentError && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-4 flex items-center gap-3">
                <AlertCircle size={20} className="text-red-400" />
                <span className="text-red-200 font-light">{paymentError}</span>
                <button 
                  onClick={clearError}
                  className="ml-auto text-red-400 hover:text-red-200"
                >
                  ×
                </button>
              </div>
            </motion.div>
          )}

          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Main Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              
              {/* Step 1: Information */}
              {currentStep === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-brand-black/85 backdrop-blur-xl rounded-2xl border border-brand-red/25 p-8"
                >
                  <h2 className="text-xl font-light text-white mb-6 tracking-wide">Contact Information</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-light text-brand-light/70 mb-2">Email *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                        placeholder="your@email.com"
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-light text-brand-light/70 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                        placeholder="+27 12 345 6789"
                      />
                    </div>
                  </div>

                  <h3 className="text-lg font-light text-brand-gold mt-8 mb-6 tracking-wide">Shipping Address</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-light text-brand-light/70 mb-2">First Name *</label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                        placeholder="John"
                      />
                      {errors.firstName && (
                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {errors.firstName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-light text-brand-light/70 mb-2">Last Name *</label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                        placeholder="Doe"
                      />
                      {errors.lastName && (
                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-light text-brand-light/70 mb-2">Address *</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                        placeholder="123 Main Street"
                      />
                      {errors.address && (
                        <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle size={14} />
                          {errors.address}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-light text-brand-light/70 mb-2">City *</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                          placeholder="Cape Town"
                        />
                        {errors.city && (
                          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle size={14} />
                            {errors.city}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-light text-brand-light/70 mb-2">Province *</label>
                        <select
                          value={formData.province}
                          onChange={(e) => handleInputChange('province', e.target.value)}
                          className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                        >
                          <option value="" className="bg-brand-black">Select Province</option>
                          <option value="WC" className="bg-brand-black">Western Cape</option>
                          <option value="GP" className="bg-brand-black">Gauteng</option>
                          <option value="KZN" className="bg-brand-black">KwaZulu-Natal</option>
                          <option value="EC" className="bg-brand-black">Eastern Cape</option>
                          <option value="FS" className="bg-brand-black">Free State</option>
                          <option value="LP" className="bg-brand-black">Limpopo</option>
                          <option value="MP" className="bg-brand-black">Mpumalanga</option>
                          <option value="NC" className="bg-brand-black">Northern Cape</option>
                          <option value="NW" className="bg-brand-black">North West</option>
                        </select>
                        {errors.province && (
                          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle size={14} />
                            {errors.province}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-light text-brand-light/70 mb-2">Postal Code *</label>
                        <input
                          type="text"
                          value={formData.postalCode}
                          onChange={(e) => handleInputChange('postalCode', e.target.value)}
                          className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all"
                          placeholder="8001"
                        />
                        {errors.postalCode && (
                          <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle size={14} />
                            {errors.postalCode}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-light text-brand-light/70 mb-2">Order Notes (Optional)</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/50 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all resize-none"
                        placeholder="Any special instructions or notes..."
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-8">
                    <button
                      onClick={handleStepNext}
                      className="px-8 py-3 bg-brand-red hover:bg-brand-red/80 text-brand-light rounded-xl font-body font-medium transition-colors shadow-[0_20px_45px_-20px_rgba(230,0,0,0.6)]"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Payment Review */}
              {currentStep === 2 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-brand-black/85 backdrop-blur-xl rounded-2xl border border-brand-red/25 p-8"
                >
                  <h2 className="text-xl font-light text-white mb-6 tracking-wide flex items-center gap-2">
                    <CreditCard size={20} />
                    Payment via PayFast
                  </h2>
                  
                  <div className="space-y-6">
                    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                      <h3 className="font-light text-white mb-4">Order Summary</h3>
                      <div className="space-y-2 text-gray-400 font-light">
                        <div className="flex justify-between">
                          <span>Items ({cartSummary.itemCount})</span>
                          <span>{cartSummary.formattedTotal}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Shipping</span>
                          <span className="text-green-400">Free</span>
                        </div>
                        <div className="border-t border-white/10 pt-2 flex justify-between text-white text-lg">
                          <span>Total</span>
                          <span>{cartSummary.formattedTotal}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                      <h3 className="font-light text-white mb-4">Shipping Address</h3>
                      <p className="text-gray-400 font-light">
                        {formData.firstName} {formData.lastName}<br />
                        {formData.address}<br />
                        {formData.city}, {formData.province} {formData.postalCode}
                      </p>
                    </div>

                    <div className="bg-blue-500/20 backdrop-blur-sm rounded-2xl p-6 border border-blue-500/30">
                      <div className="flex items-center gap-3 mb-4">
                        <ExternalLink size={20} className="text-blue-400" />
                        <h3 className="font-light text-white">Secure Payment with PayFast</h3>
                      </div>
                      <p className="text-blue-200 font-light text-sm mb-4">
                        You will be redirected to PayFast to complete your payment securely. 
                        PayFast supports all major South African banks, credit cards, and instant EFT.
                      </p>
                      <div className="flex items-center gap-2 text-blue-200 text-sm">
                        <Shield size={16} />
                        <span>256-bit SSL encryption</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between mt-8">
                    <button
                      onClick={handleStepBack}
                      className="px-8 py-3 bg-brand-black border border-brand-red/25 text-brand-light rounded-2xl font-light transition-colors hover:border-brand-gold/40 hover:text-brand-gold"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={isProcessing}
                      className="px-8 py-3 bg-brand-red hover:bg-brand-red/80 text-brand-light rounded-xl font-body font-medium transition-colors disabled:opacity-50 flex items-center gap-2 shadow-[0_20px_45px_-20px_rgba(230,0,0,0.6)]"
                    >
                      {isProcessing ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                          />
                          Processing...
                        </>
                      ) : (
                        <>
                          <ExternalLink size={16} />
                          Pay with PayFast ({cartSummary.formattedTotal})
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Order Summary Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:sticky lg:top-24 h-fit"
            >
              <div className="bg-brand-black/85 backdrop-blur-xl rounded-2xl border border-brand-red/25 p-8">
                <h2 className="text-xl font-light text-white mb-6 tracking-wide">Your Order</h2>
                
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 bg-white/5 rounded-2xl p-4">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-16 h-16 object-contain rounded-xl bg-white/10"
                      />
                      <div className="flex-1">
                        <h3 className="text-white font-light text-sm">{item.name}</h3>
                        <p className="text-gray-400 font-light text-sm">{item.price}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-6 h-6 bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="text-white text-sm w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-6 h-6 bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="ml-auto text-red-400 hover:text-red-300 p-1"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 pt-6 space-y-3">
                  <div className="flex justify-between text-gray-400">
                    <span>Subtotal</span>
                    <span>{cartSummary.formattedTotal}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>Shipping</span>
                    <span className="text-green-400">Free</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>VAT (15%)</span>
                    <span>Included</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 flex justify-between text-white text-lg font-light">
                    <span>Total</span>
                    <span>{cartSummary.formattedTotal}</span>
                  </div>
                </div>

                {/* Trust Badges */}
                <div className="mt-8 space-y-3">
                  <div className="flex items-center gap-3 text-gray-400 text-sm">
                    <Shield size={16} strokeWidth={1.5} />
                    <span className="font-light">Secure SSL encryption</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400 text-sm">
                    <Truck size={16} strokeWidth={1.5} />
                    <span className="font-light">Free shipping nationwide</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-400 text-sm">
                    <Clock size={16} strokeWidth={1.5} />
                    <span className="font-light">2-5 business days delivery</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
